<script setup lang="ts">
import {
  URL_ADDRESS_PAGE,
  URL_SEARCH,
  URL_USER_DEALS,
  URL_USER_INBOX, URL_USER_OFFERS,
  URL_USER_ORGANIZATION_MANAGE,
  URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROPOSAL,
} from '@tools/routes/routes';
import IconManifyingGlass from '@icons/ph_magnifying-glass.svg';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconHeartStraight from '@icons/ph_heart-straight.svg';
import IconAlignTop from '@icons/ph_align-top.svg';
import IconBuildings from '@icons/ph_buildings.svg';
import { trans } from '@elements/translation.ts';
import MenuLink from '@layout/nav-v2/desktop/components/MenuLink.vue';
import Icon from '@components/Icon.vue';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import IconBriefCase from '@icons/ph_briefcase.svg';
import MenuItemGroup from '@layout/nav-v2/desktop/components/MenuItemGroup.vue';
import MenuAccountManagerContact from '@layout/nav-v2/MenuAccountManagerContact.vue';
import Divider from '@components/Divider.vue';
import MenuList from '@layout/nav-v2/desktop/components/MenuList.vue';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups';

const navbarStore = useNavbarStore();
const { isExpanded, notifications } = storeToRefs(navbarStore);
const { setCurrentDropdownOpen, isCurrentDropdownOpen } = navbarStore;

const { user } = storeToRefs(useUserProfileStore());
</script>

<template>
  <MenuList>
    <li>
      <MenuLink
        :link="URL_SEARCH"
        :is-expanded="isExpanded"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconManifyingGlass"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.search') }}
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuLink
        :link="URL_USER_INBOX"
        :is-expanded="isExpanded"
        :has-pin="notifications.unread_messages > 0"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconEnvelope"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.inbox') }}

          <template v-if="notifications.unread_messages > 0">
            ({{ notifications.unread_messages }})
          </template>
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuLink
        :link="URL_USER_PROPOSAL"
        :is-expanded="isExpanded"
        :has-pin="notifications.unread_match_proposal > 0"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconHeartStraight"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.selection_item') }}

          <template v-if="notifications.unread_match_proposal > 0">
            ({{ notifications.unread_match_proposal }})
          </template>
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuLink
        :link="URL_USER_DEALS"
        :is-expanded="isExpanded"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconAlignTop"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.deals') }}
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuItemGroup
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.PROPERTY_HOLDINGS)"
        :is-expanded="isExpanded"
        :submenus="[
          { name: trans('user_menu.offers'), link: URL_USER_OFFERS },
          { name: trans('user_menu.spaces'), link: URL_ADDRESS_PAGE },
        ]"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.PROPERTY_HOLDINGS : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconBuildings"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.property_holdings') }}
        </template>
      </MenuItemGroup>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <MenuItemGroup
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
        :is-expanded="isExpanded"
        :submenus="[
          { name: trans('user_menu.organization'), link: URL_USER_ORGANIZATION_MANAGE },
          { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
        ]"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconBriefCase"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user.menu.my_organization') }}
        </template>
      </MenuItemGroup>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li v-if="isExpanded">
      <MenuAccountManagerContact
        class="mx-3"
        :account-manager="user!.account_manager"
      />
    </li>
  </MenuList>
</template>
